import { useEffect, useRef, useState } from 'react';
import { Pause, Play, Video } from 'lucide-react';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useStageMotion } from './StageMotion';
import { Switch } from './ui/switch';

const SECTIONS = {
  ofertas: 'Mesa de sonido · trabajo en directo', profesionales: 'En el estudio · profesionales',
  empresas: 'Logística de material · empresas', actualidad: 'Tras la cámara · actualidad',
  formacion: 'Aprender en el estudio · formación', eventos: 'Escenario en directo · eventos',
  foro: 'Voces de la comunidad · foro', mercado: 'Equipo en detalle · mercado', biblioteca: 'Documentación técnica · biblioteca',
};

const Scene = ({ name }) => {
  const { user, loading } = useAuth();
  const { enabled: motion, reduced } = useStageMotion();
  const ref = useRef(null);
  const allowed = !loading && user?.videos_enabled !== false;
  const automatic = allowed && motion && !reduced && !navigator.connection?.saveData;
  const [playing, setPlaying] = useState(false);
  const [requested, setRequested] = useState(false);
  const [failed, setFailed] = useState(false);
  const active = allowed && (automatic || requested);
  const base = `/media/sections/${name}`;
  useEffect(() => { if (ref.current) { ref.current.load(); if (active) ref.current.play().catch(() => setPlaying(false)); } }, [active]);
  useEffect(() => { const hide = () => { if (document.hidden) ref.current?.pause(); }; document.addEventListener('visibilitychange', hide); return () => document.removeEventListener('visibilitychange', hide); }, []);
  const toggle = () => { if (playing) ref.current.pause(); else if (!active) setRequested(true); else ref.current.play().catch(() => setPlaying(false)); };
  return <>
    <div data-testid={`section-background-${name}`} data-no-reveal aria-hidden="true" className="section-background">
      <img src={`${base}.jpg`} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <video ref={ref} data-testid="section-background-video" poster={`${base}.jpg`} muted loop playsInline autoPlay={active} preload={active ? 'metadata' : 'none'} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onError={() => { setFailed(true); setPlaying(false); }} className={`absolute inset-0 h-full w-full object-cover ${failed ? 'hidden' : ''}`}>
        {active && <><source src={`${base}.webm`} type="video/webm" /><source src={`${base}.mp4`} type="video/mp4" /></>}
      </video>
      <div className="absolute inset-0 bg-[#0B0C0E]/80" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0B0C0E]/20 to-[#0B0C0E]" />
    </div>
    {allowed && !failed && <button data-testid="section-video-toggle" aria-label={playing ? 'Pausar vídeo de fondo' : 'Reproducir vídeo de fondo'} onClick={toggle} title={SECTIONS[name]} className="fixed bottom-4 left-4 z-30 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#121418]/95 border border-[#343B48] text-xs text-slate-300 hover:text-amber-400 sc-focus transition-colors">{playing ? <Pause size={12} /> : <Play size={12} />}<span>Vídeo de fondo</span></button>}
  </>;
};

export const SectionBackground = () => {
  const location = useLocation();
  const name = location.pathname.split('/')[1];
  const section = SECTIONS[name] ? name : null;
  useEffect(() => {
    if (section) document.documentElement.dataset.sectionVideo = section;
    else delete document.documentElement.dataset.sectionVideo;
    return () => { delete document.documentElement.dataset.sectionVideo; };
  }, [section]);
  return section ? <Scene key={section} name={section} /> : null;
};

export const VideoSettings = () => {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const change = async (value) => {
    setBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/video-preference`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ videos_enabled: value }) });
      if (!res.ok) throw new Error('No se pudo guardar la preferencia de vídeo');
      const data = await res.json();
      setUser((previous) => ({ ...previous, videos_enabled: data.videos_enabled }));
      toast.success(value ? 'Vídeos de fondo activados' : 'Vídeos de fondo desactivados');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return <section className="sc-card p-6 md:p-8 mb-6" data-testid="profile-video-settings"><div className="flex items-start gap-4"><Video className="text-amber-500 shrink-0 mt-1" size={24} /><div className="flex-1 min-w-0"><h2 id="video-settings-title" className="text-base font-semibold text-white">Vídeos de fondo</h2><p className="text-sm text-slate-400 mt-2">El vídeo original de la página principal. Desactívalo para ahorrar datos y navegar sin vídeo; se conserva una imagen fija. Las demás secciones no tienen vídeos de fondo.</p><p className="text-xs text-slate-500 mt-3">Se guarda en tu cuenta. Sin sonido. También se respeta la reducción de movimiento y el ahorro de datos de tu dispositivo.</p></div><Switch data-testid="profile-video-toggle" aria-labelledby="video-settings-title" checked={user?.videos_enabled !== false} disabled={busy} onCheckedChange={change} /></div></section>;
};
